import { Flex, Text, Image } from '@chakra-ui/react'
import React from 'react'
import { useAccount } from 'wagmi'
import type { AllTokensQuery } from 'apollo/generated/graphqlClient'
import { useAllTokensQuery } from 'apollo/generated/graphqlClient'
import { AddAssetButton } from 'components/AddAssetButton'
import { ExternalLink } from 'components/ExternalLink'
import { Link } from 'components/Link'
import { Seo } from 'components/Seo'
import { Table } from 'components/Table'
import { NOT_APPLICABLE, PAGE_LENGTH } from 'constants/common'
import { Routes } from 'constants/routes'
import { env } from 'env.client'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { useTransactionHashTruncateOptions } from 'hooks/useTransactionHashTruncateOptions'
import { DetailLayout } from 'layouts/DetailLayout/DetailLayout'
import { getTruncatedText } from 'utils/common'
import { getImageSrc } from 'utils/getImageSrc'

type AllToken = AllTokensQuery['allTokens']['data'][number]

const TokensPage = () => {
  const COLORS = useThemeColors()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()
  const { chainId } = useAccount()

  const { data: { allTokens } = {}, loading: isLoading } = useAllTokensQuery({
    variables: {
      input: {
        limit: PAGE_LENGTH,
        ...(cursor && { [direction]: cursor }),
      },
    },
  })

  return (
    <>
      <Seo title="Tokens" />
      <Table
        data={allTokens?.data ?? []}
        hasNextPage={!!allTokens?.paginationContext?.next}
        hasPreviousPage={!!allTokens?.paginationContext?.previous}
        onNextPage={() => {
          setDirection('next')
          setCursor(allTokens?.paginationContext?.next ?? '')
        }}
        onPreviousPage={() => {
          setDirection('previous')
          setCursor(allTokens?.paginationContext?.previous ?? '')
        }}
        title="Tokens"
        isLoading={isLoading}
        renderMobileRow={(row: AllToken) => (
          <Table.MobileRow key={row.id}>
            {({ isExpanded }) => (
              <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                {chainId && (
                  <Table.MobileRowCell label="Add Asset:">
                    <AddAssetButton {...row} chainId={chainId} />
                  </Table.MobileRowCell>
                )}
                <Table.MobileRowCell label="Symbol:">
                  <Flex gap={2} alignItems="center">
                    <Image
                      alt="token icon"
                      boxSize={4}
                      src={getImageSrc({
                        contractAddressL2: row?.contract_address_l2,
                        type: row.type,
                      })}
                    />
                    <Text variant="text2medium">{row.symbol}</Text>
                  </Flex>
                </Table.MobileRowCell>
                <Table.MobileRowCell label="Address L2:">
                  <Link
                    size="link2medium"
                    href={Routes.addressDetail({ hash: row.contract_address_l2, activeTab: 0 })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {getTruncatedText(
                      row.contract_address_l2,
                      ...TRANSACTION_HASH_TRUNCATE_OPTIONS
                    )}
                  </Link>
                </Table.MobileRowCell>
                <Table.AnimatedMobileRowCell label="Decimals L2:" isVisible={isExpanded}>
                  <Text variant="text2regular" color={COLORS.grey02}>
                    {row.decimals_l2}
                  </Text>
                </Table.AnimatedMobileRowCell>
                <Table.MobileRowCell label="Address L1:">
                  {row.contract_address_l1 ? (
                    <ExternalLink
                      size="link2medium"
                      href={`${env.NEXT_PUBLIC_L1_URL}/token/${row.contract_address_l1}`}
                      label={getTruncatedText(
                        row.contract_address_l1,
                        ...TRANSACTION_HASH_TRUNCATE_OPTIONS
                      )}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    />
                  ) : (
                    <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                  )}
                </Table.MobileRowCell>
                <Table.AnimatedMobileRowCell label="Decimals L1:" isVisible={isExpanded}>
                  {row.decimals_l1 ? (
                    <Text variant="text2regular" color={COLORS.grey02}>
                      {row.decimals_l1}
                    </Text>
                  ) : (
                    <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                  )}
                </Table.AnimatedMobileRowCell>
              </Flex>
            )}
          </Table.MobileRow>
        )}
        renderTableHeader={() => (
          <>
            {chainId && <Table.HeaderCell>{}</Table.HeaderCell>}
            <Table.HeaderCell>Symbol</Table.HeaderCell>
            <Table.HeaderCell>Address L2</Table.HeaderCell>
            <Table.HeaderCell>Decimals L2</Table.HeaderCell>
            <Table.HeaderCell>Address L1</Table.HeaderCell>
            <Table.HeaderCell>Decimals L1</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: AllToken) => (
          <Table.Row key={row.id}>
            {chainId && (
              <Table.RowCell>
                <AddAssetButton {...row} chainId={chainId} />
              </Table.RowCell>
            )}
            <Table.RowCell>
              <Text variant="text2medium">
                <Flex gap={2} alignItems="center">
                  <Image
                    alt="token icon"
                    boxSize={6}
                    src={getImageSrc({
                      contractAddressL2: row?.contract_address_l2,
                      type: row.type,
                    })}
                  />
                  {row.symbol}
                </Flex>
              </Text>
            </Table.RowCell>
            <Table.RowCell>
              <Link
                size="link2medium"
                href={Routes.addressDetail({ hash: row.contract_address_l2, activeTab: 0 })}
              >
                {row.contract_address_l2}
              </Link>
            </Table.RowCell>
            <Table.RowCell>
              <Text variant="text2regular" color={COLORS.grey02}>
                {row.decimals_l2}
              </Text>
            </Table.RowCell>
            <Table.RowCell>
              {row.contract_address_l1 ? (
                <ExternalLink
                  size="link2medium"
                  href={`${env.NEXT_PUBLIC_L1_URL}/token/${row.contract_address_l1}`}
                  label={row.contract_address_l1}
                />
              ) : (
                <Text variant="text3regular">{NOT_APPLICABLE}</Text>
              )}
            </Table.RowCell>
            <Table.RowCell>
              {row.decimals_l1 ? (
                <Text variant="text2regular" color={COLORS.grey02}>
                  {row.decimals_l1}
                </Text>
              ) : (
                <Text variant="text3regular">{NOT_APPLICABLE}</Text>
              )}
            </Table.RowCell>
          </Table.Row>
        )}
      />
    </>
  )
}

TokensPage.getLayout = (page: React.ReactNode) => <DetailLayout>{page}</DetailLayout>

export default TokensPage
